export type PageName = 'main' | 'structure' | 'faq' | 'emailAlerts' | 'analysts' | 'financials' | 'contacts' |
    'events' | 'meetings' | 'sec' | 'stocks' | 'directors' | 'notFound' | 'pressReleases' |
    'ourPrinciples' | 'keyDocuments' | 'overview' | 'management' | 'faqGovernance' | 'contactsGovernance' |
    'keyBusinesses' | 'webcast' | 'latestPresentation' | 'about' | 'aboutMarkets' | 'aboutGrowth' |
    'aboutDiversification' | 'aboutEcosystem' | 'aboutInnovations' | 'aboutTeamAndTech' | 'presentations' | 'videoInvestors' |
    'videoConferences' | 'blog' | 'blogPost' | 'diversity' | 'prospectus';

export type SvgIconId = 'arrow' | 'arrow-external' | 'laptop' | 'business-woman' | 'taxi' | 'box-mini' | 'globe' | 'gear' | 'handshake' |
    'button-hand' | 'star' | 'cloud' | 'roadmap' | 'box' | 'aim' | 'hourglass' | 'group-people' | 'shopping-cart' |
    'coins' | 'steering-wheel' | 'rover' | 'microphone' | 'dashcam' | 'phone-selfie' | 'passport' | 'network' |
    'hierarchy' | 'megafon' | 'compare-true' | 'compare-false' | 'time' | 'art' | 'gpt' | 'neuro-service' | 'neural-network';

export type mainDocsName = 'databook' | 'latest-annual-report' | 'latest-presentation';

export interface MenuItem {
    id: string;
    url: string;
    title: string;
    icon?: string;
    isCurrent?: boolean;
    openNewTab: boolean;
    visible: boolean;
}

export interface MenuListItem {
    id: string;
    url?: string;
    title: string;
    isCurrent?: boolean;
    urgent?: boolean;
    items?: MenuItem[];
}

export interface ServiceItem {
    id: string;
    href: string;
    title: string;
    isCurrent?: boolean;
}

export interface LangSwitcherItem {
    lang: string;
    href: string;
    isCurrent: boolean;
}

export type PhoneNumber = string;
export type EmailData = string;
export type Address = string;

export interface OfficeContactsItem {
    title?: string;
    address?: Address;
    phone?: PhoneNumber;
    fax?: PhoneNumber;
    email?: EmailData;
}

export interface OfficeContacts {
    ir: OfficeContactsItem;
    pr: OfficeContactsItem;
    adv: OfficeContactsItem;
    sustainability: OfficeContactsItem;
    secretary: OfficeContactsItem;
    headquaters: OfficeContactsItem;
}
export interface ContactSectionAddress {
    name?: string;
    text?: string;
    address?: string;
    contact: OfficeContactsItem;
    contacts: OfficeContacts;
}

export interface ContactSectionForm {
    title?: string;
    text?: string;
    buttonText?: string;
    buttonLink?: string;
}

export interface SocialItem {
    text?: string;
    url?: string;
    icon?: string;
}

export interface ContactSocials {
    title?: string;
    items: SocialItem[];
}

export interface PrefooterData {
    moscowContacts: ContactSectionAddress;
    ask: ContactSectionForm;
    subscribe: ContactSectionForm;
    additionalInfo: string;
    socials: ContactSocials;
}

export interface BlogData {
    blogBunker: {
        title: string;
    };
    tags: PostTag[];
    blogItems: BlogPostItem[];
    selectedTag: string;
}

export interface BunkerDictionary {
    [key: string]: string;
}

export interface CommonData {
    yandexMainPageTitle: string;
    yandexMainPageUrl: string;
    isLangSwitcherShow: boolean;
    seo: {
        description: string;
        favicon: string;
        twittercard: string;
        og: {
            image: string;
            width: string;
            height: string;
        };
        icons: {
            [key: string]: string;
        }
    };
}

export interface FooterData {
    legalDocuments: Link;
}

export interface WarningData {
    isShow: boolean;
    id: string;
    storage?: 'local' | 'session';
    title?: string;
    text: string;
    button?: string;
}

export interface MetrikaData {
    metrikaId?: number;
    metrikaOptions: {
        clickmap?: boolean,
        trackLinks?: boolean,
        webvisor?: boolean,
        accurateTrackBounce?: boolean
    };
}

export interface BundleProps {
    data: BundleData;
}

export interface Analyst {
    firm: string;
    person: string;
    email: string;
}

export interface BunkerAnalysts {
    title: string;
    analysts: Analyst[];
    text: string;
}

export interface BunkerBlog {
    isShow: boolean;
    title: string;
    tags: string[];
}

export const COMMITTEE_TYPES = ['all', 'governance', 'interest', 'audit', 'investment', 'nominating', 'compensation'];

export type DirectorCommittee = typeof COMMITTEE_TYPES[number];

export interface DirectorData {
    person: string;
    role: string;
    photo: string;
    bio: string;
    chairpersonCommittees: DirectorCommittee[];
    memberCommittees: DirectorCommittee[];
}

export interface BunkerDirectors {
    title: string;
    isShowCommittees: boolean;
    directors: DirectorData[];
}

export interface BunkerDiversity {
    title: string;
    table: DiversityTable;
}

export interface BunkerStocks {
    isShow: boolean;
    isShowIframe: boolean;
    stocksUrl: string;
    title: string;
    text?: string;
    url?: string;
}

export interface BunkerContact {
    title: string;
}

export interface BunkerDocuments {
    title: string;
    text: string;
    corporateDocs: DocItem[];
    chartersDocs: DocItem[];
}

export interface BunkerDocLink {
    link: string;
}

export interface BunkerWebcast {
    title: string;
    link?: string;
}

export interface ProspectusItem {
    title: string;
    docs: DocItem[];
}

export interface BunkerProspectus {
    isShow: boolean;
    title: string;
    text?: string;
    pageName: string;
    items: ProspectusItem[];
}

export interface BunkerAgreementDisclaimer {
    text: string;
    html: string;
    buttons: {
        accept: {
            text: string;
        };
        decline: {
            text: string;
        }
    };
}

export interface BunkerLatestPresentation {
    title: string;
    text?: string;
    docs?: DocItem[];
}

export interface BunkerOverview {
    title: string;
    text: string;
}

export interface BunkerContacts {
    title: string;
}

export interface PersonInfo {
    photo: string;
    name: string;
    position: string;
    bio: string;
}

export interface BunkerManagement {
    title: string;
    list: PersonInfo[];
}

export interface BunkerGovernance {
    title: string;
    documents?: BunkerDocuments;
    overview?: BunkerOverview;
    contacts?: BunkerContacts;
    directors?: BunkerDirectors;
    diversity?: BunkerDiversity;
    faq?: FaqListItem;
    management?: BunkerManagement;
}

export interface TypeMeetingData {
    title: string;
    list?: OccasionData[];
}

export type TypeMeetingId = 'all' | 'annual' | 'extraordinary';

export interface OccasionData {
    title: string;
    location?: string;
    docs: DocItem[];
    dateStart: string;
    dateEnd?: string;
    timeStart?: Time;
    timeEnd?: Time;
}

export interface BunkerFinancialQuarter {
    title: string;
    date: string;
    docs: DocItem[];
    url?: string;
    html?: string;
    releaseid?: string;
    linkPdf?: string;
    signature?: string;
}

export const REPORT_NAMES = ['q1', 'q2', 'q3', 'q4', 'reaffirms'];

export type BunkerFinancialType = typeof REPORT_NAMES[number];

export type BunkerFinancialYear = {
    [key in BunkerFinancialType]: BunkerFinancialQuarter;
};

export interface BunkerMeetings {
    title: string;
    all: string;
    pageName: PageName;
    egm: TypeMeetingData;
    agm: TypeMeetingData;
    current?: string;
}

export type DocType = 'presentation' | 'slides' | 'webcast';

export interface DocItem {
    title: string;
    type?: DocType;
    link?: string;
    format?: string;
    description?: string;
}

export interface SecFiling {
    title: string;
    formType: string;
    pubDate: string;
    periodDate?: string;
    filingGroup: string;
    company: string;
    issuer: string;
    guid: string;
    documentFormatFiles: DocItem[];
    xbrlSequence?: DocItem[];
}

export interface Time {
    value?: string;
    zone?: string;
}

export interface EventsYear {
    title: string;
    currentYearData: OccasionData[];
}

export interface FaqQuestionItem {
    question?: string;
    answer: string;
}

export interface FaqListItem {
    title: string;
    questions: FaqQuestionItem[];
}

export interface BunkerFaq {
    title: string;
    list: FaqListItem[];
}

export interface BunkerPrinciplesData {
    title: string;
    imageUrl: string;
    list: string[];
}

export interface BunkerPrinciples {
    [key: string]: BunkerPrinciplesData;
}

export const enum BunkerKeyBusinessesSubnodeType {
    Nope = 'nope',
    Segment = 'segment',
    Service = 'service'
}

export interface BunkerKeyBusinessesNode {
    title: string;
    content: string;
    'subnodes-type': BunkerKeyBusinessesSubnodeType;
    subnodes: BunkerKeyBusinessesNode[];
}

interface BunkerKeyBusinesses {
    title: string;
    help?: string;
    note: string;
    structure: BunkerKeyBusinessesNode[];
}

interface BunkerContactsGovernance {
    title: string;
}

export interface SharesIncludingData {
    classA: string;
    preferredA: string;
    preferredB: string;
}

export interface SharesData {
    totalLabel: string;
    total: string;
    including?: SharesIncludingData;
}

export interface ShareholderPercents {
    title: string;
    amount: string;
}

export interface ShareholdersDataItem {
    shareholder: string;
    capital: string;
}

export interface ShareholdersData {
    items: ShareholdersDataItem[];
    total: ShareholderPercents;
    note: string;
}

export interface BunkerStructure {
    title: string;
    date: string;
    shares: SharesData;
    shareholders: ShareholdersData;
    dictionary: {
        [key: string]: string;
    };
}

export interface Link {
    title: string;
    url: string;
    isTargetBlank?: boolean;
}

interface MapNote {
    office: string;
    location: string;
    registration: string;
}

export interface BusinessUnitsData {
    id: string;
    title: string;
    logo: string;
    url: string;
}

interface MainGeographyData {
    isVisible: boolean;
    title: string;
    countries: Array<{
        country: string;
        type: 'office' | 'location' | 'registration';
        description: string;
        x: number;
        y: number;
        businessUnits: BusinessUnitsData[];
    }>;
    'office-badge': string;
    'location-badge': string;
    'registration-badge': string;
    'business-units-label': string;
    note: MapNote;
}

interface MainGeographyBunkerData {
    isVisible: boolean;
    title: string;
    countries: Array<{
        country: string;
        type: 'office' | 'location' | 'registration';
        description: string;
        x: number;
        y: number;
        businessUnits: string[];
    }>;
    'office-badge': string;
    'location-badge': string;
    'registration-badge': string;
    'business-units-label': string;
    note: MapNote;
}

export interface MainInfoData {
    isVisible: boolean;
    title: string;
    text: string;
    link: string;
}

export interface ProfileData {
    title: string;
    text: string;
    about?: Link;
    businesses: Link;
    contact: Link;
    isShowStocks: boolean;
}

export interface BunkerNotFound {
    title: string;
    text: string;
}

export enum MainImage {
    Slide1x480 = 'slide-1-480',
    Slide2x480 = 'slide-2-480',
    Slide3x480 = 'slide-3-480',
    Slide4x480 = 'slide-4-480',
    Slide5x480 = 'slide-5-480',
    Slide6x480 = 'slide-6-480',
    Slide1x2560 = 'slide-1-2560',
    Slide2x2560 = 'slide-2-2560',
    Slide3x2560 = 'slide-3-2560',
    Slide4x2560 = 'slide-4-2560',
    Slide5x2560 = 'slide-5-2560',
    Slide6x2560 = 'slide-6-2560',
}

export interface BunkerMainSlider {
    '480': {
        image: MainImage;
        position: string;
    };
    '2560': {
        image: MainImage;
        position: string;
    };
}

export interface PropsMainSlider {
    '480': {
        image: string;
        position: string;
    };
    '2560': {
        image: string;
        position: string;
    };
}

export type BunkerMainImages = Record<MainImage, string>;

export interface BunkerMain extends BunkerMainImages {
    title: string;
    slider: BunkerMainSlider[];
    links: Link[];
    financials: {
        title: string;
    };
    releases: {
        title: string;
    };
    history: MainInfoData;
    geography: MainGeographyBunkerData;
    principles: MainInfoData;
    profile: ProfileData;
}

export interface BunkerPressReleases {
    title: string;
    messages: MessagesText;
    archive?: Link;
    rss: {
        title: string;
        link: string;
        description: string;
        text: string;
    };
}

export interface FinancialsYear {
    reports: BunkerFinancialYear;
}

export interface BunkerEvents {
    [key: string]: OccasionData[];
}

export interface PresentationsListItem {
    title: string;
    dateStart: string;
    docs: DocItem[];
}

export interface BunkerPresentations {
    title: string;
    list: PresentationsListItem[];
}

export interface BunkerFinancials {
    [key: string]: BunkerFinancialYear;
}

export interface BunkerFinancialsArchive {
    [key: string]: BunkerFinancialYear;
}

export interface SecFilingType {
    groupID: string;
    groupName: string;
    groupFilings: string[];
}

export interface PostTag {
    url?: string;
    slug: string;
    displayName: string;
}

export interface PostImage {
    orig?: { fullPath: string };
}

export interface PressReleasesData {
    list: PressReleaseItemData[];
    hasMore?: boolean;
    lastId?: string;
}

export interface PressReleaseItemData {
    _id: string;
    url: string;
    title: string;
    date: string;

    markup?: string;
    annotation?: string;
    socialImage?: string;
    tags: PostTag[];
    description: string;
    keywords: string;

    ignoreInIndex: boolean;
    ignoreByWidget: boolean;
    financialResults: boolean;
    isMigrated?: boolean;
    signature?: string;
    hasNext?: boolean;
}

export interface BlogItemsData {
    list: BlogPostItem[];
    hasMore?: boolean;
    lastId?: string;
}

export interface BlogPostItem {
    _id: string;
    url: string;
    title: string;
    date: string;

    markup?: string;
    annotation?: string;
    socialImage?: string;
    tags: PostTag[];
    description: string;
    keywords: string;
    hasNext?: boolean;
}

export interface CommitteesData {
    [key: string]: string;
}

enum AdvantageImage {
    Diversification = 'diversification',
    Ecosystem = 'ecosystem',
    Growth = 'growth',
    Innovations = 'innovations',
    Markets = 'markets',
    TeamAndTech = 'team-and-tech'
}

export interface AdvantageData {
    id: string;
    label: string;
    description: string;
    url: string;
    image: AdvantageImage;
}

export type AdvantageProps = Omit<AdvantageData, 'image'> & { image: string };

export type BunkerAboutImages = Record<AdvantageImage, string>;

export interface BunkerAbout extends BunkerAboutImages {
    title: string;
    advantages: AdvantageData[];
}

export interface AboutMarketsItem {
    name: string;
    units: string[];
    subtitle: string;
    description: string;
    opponents: string;
}

export interface BunkerAboutMarkets {
    title: string;
    text: string;
    ['table-header']: {
        direction: {
            label: string;
        };
        units: {
            label: string;
        };
        opponents: {
            label: string;
            help: string;
        };
    };
    markets: AboutMarketsItem[];
}

export interface BunkerAboutGrowth extends AboutGrowthImage {
    title: string;
    text: string;
    card: {
        chart: ChartData;
    };
}

export interface AboutInnovationsImages {
    'banner-desktop': string;
    'banner-phone': string;
}

export interface InnovationsDataItem {
    id: string;
    icon: SvgIconId;
    text: string;
    help?: string;
}

export interface BunkerAboutInnovations extends AboutInnovationsImages {
    title: string;
    text: string;
    'self-driving': {
        title: string;
        data: InnovationsDataItem[];
    };
    'delivery-robots': {
        title: string;
        data: InnovationsDataItem[];
    };
    'neural-networks': {
        title: string;
        data: InnovationsDataItem[];
    };
}

interface AboutTeamAndTechImages {
    'banner-team-desktop': string;
    'banner-team-phone': string;
    'banner-tech-desktop': string;
    'banner-tech-phone': string;
}

export interface PeopleData {
    id: string;
    icon: SvgIconId;
    numbers: string;
    text: string;
}

export interface KeyTechData {
    id: string;
    icon: SvgIconId;
    title: string;
}

export interface BunkerAboutTeamAndTech extends AboutTeamAndTechImages {
    title: string;
    text: string;
    team: {
        title: string;
        data: string[];
    };
    people: {
        title: string;
        data: PeopleData[];
    };
    tech: {
        title: string;
        data: string[];
    };
    'key-tech': {
        title: string;
        data: KeyTechData[];
    };
}

export type AboutMarketsItemProps = Omit<AboutMarketsItem, 'units'> & {
    logoList: string[];
    title: string;
};

export type PageAboutMarketsProps = Omit<BunkerAboutMarkets, 'markets'> & {
    markets: AboutMarketsItemProps[];
};

export interface ChartData {
    title?: string;
}

export interface AboutGrowthImage {
    'growth-desktop': string;
    'growth-phone': string;
}

export type PageAboutGrowthProps = Pick<BunkerAboutGrowth, 'title' | 'text' | 'card'> & {
    image: AboutGrowthImage;
};

export interface NumberOfServiceUsers {
    logo: string;
    number: string;
    description: string;
    help: string;
}

export interface AboutEcosystemImage {
    'scheme-desktop': string;
    'scheme-phone': string;
}

export interface BunkerAboutEcosystem extends AboutEcosystemImage {
    title: string;
    text: string;
    services: {
        title: string;
    };
    'active-users': {
        title: string;
        'number-users': NumberOfServiceUsers[];
    };
}

export type PageAboutEcosystemProps = Pick<BunkerAboutEcosystem, 'title' | 'text' | 'services'> & {
    activeUsers: {
        title: string;
        numberUsers: NumberOfServiceUsers[];
    };
    images: AboutEcosystemImage;
};

export interface PageAboutProps {
    title: string;
    advantages: AdvantageProps[];
}

export enum DiversificationCompanyUnits {
    Search = 'search',
    Advertising = 'advertising',
    Maps = 'maps',
    Ridetech = 'ridetech',
    Foodtech = 'foodtech',
    Ecom = 'ecom',
    Cloud = 'cloud',
    Media = 'media',
    Drones = 'drones',
    Financetech = 'financetech',
}

interface DiversificationCompanyUnitsTypes {
    has: boolean;
    help: string;
}

export interface DiversificationCompany extends Record<DiversificationCompanyUnits, DiversificationCompanyUnitsTypes> {
    id: string;
    name: string;
}

export interface AboutDiversificationImage {
    'profit-desktop': string;
    'profit-phone': string;
    'segment-desktop': string;
    'segment-phone': string;
}

export interface BunkerAboutDiversification extends AboutDiversificationImage {
    title: string;
    text: string;
    company: DiversificationCompany[];
    charts: {
        title: string;
        text: string;
        profit: string;
        segment: string;
    };
    dictionary: Record<DiversificationCompanyUnits, string>;
}

export enum VideoType {
    Youtube = 'youtube',
    VH = 'vh',
}

export interface BunkerVideoItem {
    id: string;
    type: VideoType;
    url: string;
    date: string;
    title: string;
    description: string;
    duration: string;
}

export interface BunkerVideo {
    title: string;
    conferences?: BunkerVideoContent;
    investors?: BunkerVideoContent;
}

export interface BunkerVideoContent {
    title: string;
    video: BunkerVideoItem[];
}

export interface BunkerDataLang {
    common: {
        menu: {
            items: MenuListItem[];
        };
        prefooter: PrefooterData;
        footer: FooterData;
        warning: WarningData;
        main: CommonData;
        services: {
            items: ServiceItem[];
        };
    };
    dictionary: BunkerDictionary;
    libs: {
        'sec_groups': {
            items: SecFilingType[];
        },
        committees: CommitteesData;
        'business_units'?: {
            items: BusinessUnitsData[];
        }
    };
    images: {
        [key: string]: string;
    };
    pages: {
        about?: BunkerAbout;
        ['about-ecosystem']?: BunkerAboutEcosystem;
        ['about-markets']?: BunkerAboutMarkets;
        ['about-growth']?: BunkerAboutGrowth;
        ['about-diversification']?: BunkerAboutDiversification;
        ['about-innovations']?: BunkerAboutInnovations;
        ['about-team-and-tech']?: BunkerAboutTeamAndTech;
        analysts?: BunkerAnalysts;
        blog?: BunkerBlog;
        directors?: BunkerDirectors;
        documents?: BunkerDocuments;
        governance?: BunkerGovernance;
        meetings?: BunkerMeetings;
        events?: BunkerEvents;
        presentations?: BunkerPresentations;
        financials?: BunkerFinancials;
        ['financials-archive']?: BunkerFinancialsArchive;
        faq?: BunkerFaq;
        structure?: BunkerStructure;
        principles?: BunkerPrinciples;
        main?: BunkerMain;
        'not-found'?: BunkerNotFound;
        'press-releases'?: BunkerPressReleases;
        'key-businesses'?: BunkerKeyBusinesses;
        'latest-presentation'?: BunkerDocLink;
        'latest-annual-report'?: BunkerDocLink;
        databook?: BunkerDocLink;
        prospectus?: BunkerProspectus;
        stocks?: BunkerStocks;
        contact?: BunkerContact;
        video?: BunkerVideo;
    };
}

export interface BunkerData {
    [key: string]: BunkerDataLang;
}

export interface FilingType {
    id: string;
    value: string;
}

export interface PageSecProps {
    groupId: string;
    currentYear: string;
    years: string[];
    types: FilingType[];
    yearFilings?: SecFiling[];
    singleFiling?: SecFiling;
    sectionTitle?: string;
}

export interface PageStructureProps {
    sectionTitle?: string;
    structure?: BunkerStructure;
    pageName: PageName;
}

export interface PagePresentationsProps {
    title: string;
    list: PresentationsListItem[];
}

export interface PageEventsProps {
    title: string;
    years: string[];
    currentYear: string;
    yearEvents?: OccasionData[];
}

export interface PageFinancialsProps {
    title: string;
    tabArchive?: string;
    years: string[];
    currentYear: string;
    messages: MessagesText;
    requestedArchive?: boolean;
    yearReports?: BunkerFinancialYear;
    archiveData?: BunkerFinancialsArchive;
    quarterReport?: BunkerFinancialQuarter;
}

export interface MessagesText {
    notFound: string;
    notAvailable: string;
}

export interface PagePressReleasesProps {
    title?: string;
    items?: PressReleaseItemData[];
    item?: PressReleaseItemData;
    currentYear: string;
    years?: string[];
    archive?: Link;
    messages: MessagesText;
}

export interface PagePrinciplesProps {
    title: string;
    section: BunkerPrinciplesData;
    sectionId: string;
}

export interface PageMainProps {
    title: string;
    slider: PropsMainSlider[];
    stocksTeaserUrl: string;
    profile: ProfileData;
    financials: {
        title: string;
        lastFinancialReports: BunkerFinancialYear;
    };
    releases: {
        title: string;
        pressReleases: PressReleaseItemData[];
    };
    principles: MainInfoData;
    geography: MainGeographyData;
    history: MainInfoData;
    links: Link[];
}

export interface PageDirectorsProps {
    title: string;
    isShowCommittees: boolean;
    list: DirectorData[];
    committees: CommitteesData;
    selectedCommittee?: DirectorCommittee;
}

export interface DiversityTable {
    caption: string;
    date: string;
    country: string;
    issuer: string;
    disclosure: string;
    count: string;
    identity: {
        female: string;
        male: string;
        nonbinary: string;
        dnd: string;
    };
    demographic: {
        underrepresented: string;
        lgbtq: string;
        dnd: string;
    };
}

export interface PageDiversityProps {
    sectionTitle: string;
    title: string;
    table: DiversityTable;
}

export interface PageNotFoundProps {
    title: string;
    text: string;
}

export interface PageAboutDiversificationProps {
    title: string;
    text: string;
    company: DiversificationCompany[];
    charts: {
        title: string;
        text: string;
        profit: string;
        segment: string;
    };
    dictionary: Record<DiversificationCompanyUnits, string>;
    image: AboutDiversificationImage;
}

export interface PageAboutInnovationsProps {
    title: string;
    text: string;
    selfDriving: {
        title: string;
        data: InnovationsDataItem[];
    };
    deliveryRobots: {
        title: string;
        data: InnovationsDataItem[];
    };
    neuralNetworks: {
        title: string;
        data: InnovationsDataItem[];
    };
    image: AboutInnovationsImages;
}

export interface PageAboutTeamAndTechProps {
    title: string;
    text: string;
    team: {
        title: string;
        data: string[];
    };
    people: {
        title: string;
        data: PeopleData[];
    };
    tech: {
        title: string;
        data: string[];
    };
    keyTech: {
        title: string;
        data: KeyTechData[];
    };
    image: AboutTeamAndTechImages;
}

export interface BundleData {
    readonly about: PageAboutProps;
    readonly aboutEcosystem: PageAboutEcosystemProps;
    readonly aboutGrowth: PageAboutGrowthProps;
    readonly aboutMarkets: PageAboutMarketsProps;
    readonly aboutDiversification: PageAboutDiversificationProps;
    readonly aboutInnovations: PageAboutInnovationsProps;
    readonly aboutTeamAndTech: PageAboutTeamAndTechProps;
    readonly analysts: BunkerAnalysts;
    readonly documents: BunkerDocuments;
    readonly overview: BunkerOverview;
    readonly governance: BunkerGovernance;
    readonly management: BunkerManagement;
    readonly meetings: BunkerMeetings;
    readonly faq: BunkerFaq;
    readonly faqGovernance: FaqListItem;
    readonly keyBusinesses: BunkerKeyBusinesses;
    readonly contactsGovernance: BunkerContactsGovernance;

    readonly post: PressReleaseItemData;
    readonly presentations: PagePresentationsProps;
    readonly events: PageEventsProps;
    readonly financials: PageFinancialsProps;
    readonly sec: PageSecProps;
    readonly pressReleases: PagePressReleasesProps;
    readonly main: PageMainProps;
    readonly structure: BunkerStructure;
    readonly latestPresentation: BunkerLatestPresentation;
    readonly webcast: BunkerWebcast;
    readonly principles: PagePrinciplesProps;
    readonly directors: PageDirectorsProps;
    readonly diversity: PageDiversityProps;
    readonly notFound: PageNotFoundProps;
    readonly video: BunkerVideo;
    readonly videoConferences: BunkerVideoContent;
    readonly videoInvestors: BunkerVideoContent;
    readonly prospectus: BunkerProspectus;

    sectionTitle: string;
    title: string;
    baseUrl: string;
    nonce: string;
    dictionary: BunkerDictionary;
    lang: 'ru' | 'en';
    faviconLang: 'ru' | 'en';
    yandexServiceId: string;
    pageName: PageName;
    langdetect: 'ru' | 'en';
    isTestingEnv: boolean;
    prefooter: PrefooterData;
    footer: FooterData;
    metrika: MetrikaData;
    menu: MenuListItem[];
    services: ServiceItem[];
    common: CommonData;
    stocksUrl: string;
    text: string;
    isShowIframe: boolean;
    stocksTeaserUrl: string;
    emailFormUrl: string;
    askFormUrl: string;
    boardFormUrl: string;
    yandexFormEmbedPath: string;
    langSwitcher: LangSwitcherItem[];
    messages: MessagesText;
    archive: Link;
    blogData: BlogData;
    warning: WarningData;
    hasPageMenu?: boolean;
}
